'use client';

import { createClient } from '@/lib/supabase/client';

const BUCKET_NAME = 'course-materials';

// File size limits
export const FILE_SIZE_LIMITS = {
  video: 500 * 1024 * 1024,      // 500 MB
  document: 50 * 1024 * 1024,    // 50 MB
  pdf: 50 * 1024 * 1024,         // 50 MB
  presentation: 50 * 1024 * 1024, // 50 MB
  html: 20 * 1024 * 1024,        // 20 MB
};

export type MaterialType = 'video' | 'pdf' | 'document' | 'presentation' | 'html';

// Allowed MIME types for upload
const ALLOWED_MIME_TYPES: Record<string, string> = {
  // Documents
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.odt': 'application/vnd.oasis.opendocument.text',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.odp': 'application/vnd.oasis.opendocument.presentation',
  // HTML
  '.html': 'text/html',
  '.htm': 'text/html',
  // Videos
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
  '.mov': 'video/quicktime',
  '.avi': 'video/x-msvideo',
  '.mkv': 'video/x-matroska',
};

function getMimeType(filename: string): string {
  const ext = filename.toLowerCase().match(/\.[^.]+$/)?.[0] || '';
  return ALLOWED_MIME_TYPES[ext] || 'application/octet-stream';
}

/**
 * Validate file size before upload.
 * Returns an error message string if invalid, or null if valid.
 */
export function validateFileSize(file: File, materialType: MaterialType): string | null {
  const limit = FILE_SIZE_LIMITS[materialType] ?? FILE_SIZE_LIMITS.document;
  if (file.size > limit) {
    const limitMB = Math.round(limit / 1024 / 1024);
    const fileMB = (file.size / 1024 / 1024).toFixed(1);
    return `File is too large (${fileMB} MB). Maximum allowed size for ${materialType} files is ${limitMB} MB.`;
  }
  return null;
}

export const materialStorageService = {
  /**
   * Upload a file to Supabase Storage under course-materials/{courseId}/{lecturerId}/{filename}
   * Returns the public download URL or throws an error with a descriptive message.
   * Optional onProgress callback receives a number 0–100.
   */
  async uploadFile(
    file: File,
    courseId: string,
    lecturerId: string,
    onProgress?: (percent: number) => void
  ): Promise<string | null> {
    const supabase = createClient();

    // Sanitize filename: replace spaces with underscores, keep extension
    const safeName = file.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '');
    const timestamp = Date.now();
    const storagePath = `${courseId}/${lecturerId}/${timestamp}_${safeName}`;
    const contentType = getMimeType(file.name) || file.type || 'application/octet-stream';

    // If progress tracking is requested, use XHR for real upload progress
    if (onProgress) {
      return new Promise<string | null>((resolve, reject) => {
        // Get the Supabase storage upload URL
        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
        const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
        if (!supabaseUrl || !supabaseKey) {
          reject(new Error('Supabase configuration missing.'));
          return;
        }

        // Encode each path segment individually to preserve '/' separators
        const encodedPath = storagePath.split('/').map(encodeURIComponent).join('/');
        const uploadUrl = `${supabaseUrl}/storage/v1/object/${BUCKET_NAME}/${encodedPath}`;

        const xhr = new XMLHttpRequest();
        xhr.open('POST', uploadUrl, true);
        xhr.setRequestHeader('Authorization', `Bearer ${supabaseKey}`);
        xhr.setRequestHeader('apikey', supabaseKey);
        xhr.setRequestHeader('Content-Type', contentType);
        xhr.setRequestHeader('x-upsert', 'false');
        xhr.setRequestHeader('cache-control', '3600');

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            onProgress(percent);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            onProgress(100);
            // Get public URL
            const { data } = supabase.storage
              .from(BUCKET_NAME)
              .getPublicUrl(storagePath);
            if (data?.publicUrl) {
              resolve(data.publicUrl);
            } else {
              reject(new Error('File uploaded but could not retrieve public URL.'));
            }
          } else {
            let errMsg = `Upload failed (HTTP ${xhr.status})`;
            try {
              const parsed = JSON.parse(xhr.responseText);
              const msg = parsed?.message || parsed?.error || '';
              if (msg.includes('row-level security') || msg.includes('policy') || xhr.status === 403) {
                errMsg = 'Storage permission denied. Please contact the administrator to fix storage permissions.';
              } else if (msg.includes('mime type') || msg.includes('content type')) {
                errMsg = `File type not allowed: ${contentType}. Please upload a PDF, document, presentation, or video file.`;
              } else if (msg) {
                errMsg = `Upload failed: ${msg}`;
              }
            } catch {
              // ignore parse error
            }
            reject(new Error(errMsg));
          }
        };

        xhr.onerror = () => {
          reject(new Error('Network error during upload. Please check your connection and try again.'));
        };

        xhr.send(file);
      });
    }

    // Fallback: use Supabase SDK (no progress)
    const { data: uploadData, error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType,
      });

    if (error) {
      console.error('[materialStorageService] Upload error:', error.message, error);
      if (error.message?.includes('row-level security') || error.message?.includes('policy') || error.message?.includes('Unauthorized') || error.message?.includes('403')) {
        throw new Error('Storage permission denied. Please contact the administrator to fix storage permissions.');
      }
      if (error.message?.includes('mime type') || error.message?.includes('content type')) {
        throw new Error(`File type not allowed: ${contentType}. Please upload a PDF, document, presentation, or video file.`);
      }
      if (error.message?.includes('size') || error.message?.includes('too large')) {
        throw new Error(`File is too large. Maximum size is 500 MB for videos and 50 MB for documents.`);
      }
      throw new Error(`Upload failed: ${error.message}`);
    }

    if (!uploadData?.path) {
      throw new Error('Upload succeeded but no file path was returned.');
    }

    const { data } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(storagePath);

    if (!data?.publicUrl) {
      throw new Error('File uploaded but could not retrieve public URL.');
    }

    return data.publicUrl;
  },

  /**
   * Delete a file from Supabase Storage given its public URL.
   */
  async deleteFile(publicUrl: string): Promise<boolean> {
    try {
      const supabase = createClient();
      const url = new URL(publicUrl);
      const pathParts = url.pathname.split(`/object/public/${BUCKET_NAME}/`);
      if (pathParts.length < 2) return false;
      const storagePath = decodeURIComponent(pathParts[1]);

      const { error } = await supabase.storage
        .from(BUCKET_NAME)
        .remove([storagePath]);

      if (error) {
        console.warn('[materialStorageService] Delete error:', error.message);
        return false;
      }
      return true;
    } catch {
      return false;
    }
  },
};
